// src/finance/components/FinanceModal.jsx
// Modal nativo: bottom sheet en móvil · dialog en desktop

import React, { useMemo } from "react";
import { DollarSign } from "lucide-react";
import { CATEGORIAS_RECEITA, CATEGORIAS_DESPESA } from "../constants";
import NativeSheet from "../../components/ui/NativeSheet";
import { useIsMobile } from "../../hooks/useMediaQuery";

const FinanceModal = ({ isOpen, onClose, onSave, form, setForm, isMobile: isMobileProp }) => {
  const isMobileHook = useIsMobile();
  const isMobile = isMobileProp ?? isMobileHook;

  const categorias = useMemo(
    () => (form.type === "receita" ? CATEGORIAS_RECEITA : CATEGORIAS_DESPESA),
    [form.type]
  );

  const inputClass =
    "surface-input w-full px-3.5 py-2.5 text-[16px] md:text-sm outline-none transition-shadow";

  const labelClass = `block font-medium text-secondary mb-1.5 ${isMobile ? "text-xs" : "text-sm"}`;

  const footer = (
    <div className={`flex gap-2 ${isMobile ? "flex-col-reverse" : "justify-end"}`}>
      <button
        type="button"
        onClick={onClose}
        className={`rounded-xl font-medium border border-color text-secondary hover:bg-hover transition active:scale-[0.98] ${
          isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"
        }`}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="finance-launch-form"
        className={`bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold shadow-md shadow-emerald-600/20 transition active:scale-[0.98] ${
          isMobile ? "w-full py-3 text-sm" : "px-6 py-2.5 text-sm"
        }`}
      >
        Salvar lançamento
      </button>
    </div>
  );

  return (
    <NativeSheet
      open={isOpen}
      onClose={onClose}
      title="Novo Lançamento"
      footer={footer}
      maxWidth="max-w-md"
      headerRight={
        <span className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600">
          <DollarSign size={16} />
        </span>
      }
    >
      <form id="finance-launch-form" onSubmit={onSave} className="space-y-3.5">
        {/* Tipo segmented control estilo iOS */}
        <div>
          <label className={labelClass}>Tipo</label>
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-muted border border-color">
            {[
              { id: "receita", label: "Receita" },
              { id: "despesa", label: "Despesa" },
            ].map((opt) => {
              const active = form.type === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setForm({ ...form, type: opt.id, categoria: "" })}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-[0.98] ${
                    active
                      ? opt.id === "receita"
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "bg-red-500 text-white shadow-sm"
                      : "text-secondary hover:bg-hover"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelClass}>Categoria *</label>
          <select
            className={inputClass}
            value={form.categoria}
            onChange={(e) => setForm({ ...form, categoria: e.target.value })}
            required
          >
            <option value="">Selecione a categoria</option>
            {categorias.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Valor (R$) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              className={inputClass}
              placeholder="0,00"
              value={form.valor}
              onChange={(e) => setForm({ ...form, valor: e.target.value })}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Data *</label>
            <input
              type="date"
              className={inputClass}
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              required
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Descrição</label>
          <textarea
            className={`${inputClass} resize-none`}
            placeholder="Opcional"
            rows={isMobile ? 2 : 3}
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
          />
        </div>
      </form>
    </NativeSheet>
  );
};

export default FinanceModal;
